/**
 * Vehicles API — typed wrappers around `/v1/vehicles` (list) and brands.
 *
 * Mirrors the OpenAPI shapes in `frontend/src/types/api.ts`. Re-declared here
 * so the module compiles cleanly when the (gitignored) generated types haven't
 * been refreshed yet — it stays in sync because each field is checked against
 * `components['schemas']['Vozidlo']` whenever it is.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { pujcovnaKeys } from '@/modules/pujcovna/api';
import { getAuthHeaders } from '@/lib/auth';
import type { OjetaTerminyResponse } from '@/modules/ojeta-vozidla/api';

export interface Vehicle {
  id_vozidla: number;
  id_firmy: number;
  /** Name of the holding firma. */
  firma_nazev?: string | null;
  spz_vozu: string;
  vc_auta: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  motor: string;
  barva: string;
  prevodovka: string;
  calouneni: string;
  vin: string;
  cislo_skladove_karty: string;
  cislo_faktury: number;
  hodnota_faktury: string;
  datum_faktury: string | null;
  umisteni: string;
  stav_vozidla: string;
  cenikova_cena: string;
  nakupni_cena: string;
  fleet_zaloha: number;
  fleet_predpoklad: number;
  fleet_skutecnost: number;
  marketing_predpoklad: number;
  prodejni_cena_navrh: string;
  datum_predani: string | null;
  /** First registration / STK valid until / next service due (YYYY-MM-DD); null = not tracked. */
  prvni_registrace?: string | null;
  stk_platnost?: string | null;
  serviska?: string | null;
  poznamka: string;
  vyplaceno_fmc: string;
  cislo_zakazky: string;
  rezervace: string;
  id_zakaznika: string;
  id_leasingovky: number;
  modelovy_rok: string | null;
  lms_entita_json: string | null;
  lms_vybava_json: string | null;
  datum_posledni_zmeny: string | null;
  znacka_nazev?: string | null;
  nazev_modelu?: string | null;
  jmeno_zakaznika?: string | null;
  /** Transfer between group firmas (migration 0048). */
  preveden_do_firmy?: number | null;
  preveden_at?: string | null;
  preveden_do_firma?: string | null;
  prevzato_z_firmy?: number | null;
  prevzato_z_firma?: string | null;
  last_charge_date?: string | null;
  last_battery_level?: number | null;
  days_since_charge?: number | null;
}

interface VehicleListResponse {
  vehicles: Vehicle[];
  total: number;
  /** Whole-result breakdown by state (first page only); the crown shows it instead of counting loaded rows. */
  pocty?: { registrovany: number; skladovy: number; vyplacene: number };
}

export interface VehicleListFilters {
  brand?: string;
  model?: string;
  vin?: string;
  spz_vozu?: string;
  order_number?: string;
  search?: string;
  status?: string;
  invoice_number?: string;
  delivery_from?: string;
  delivery_to?: string;
  limit?: string;
  offset?: string;
  /** `skupina`: every firma of the group the caller can read (plan §6.5). Default: the active firma. */
  rozsah?: 'aktivni' | 'skupina';
}

function buildQuery(filters: VehicleListFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useVehiclesQuery(filters: VehicleListFilters) {
  return useQuery({
    queryKey: ['vehicles', filters],
    queryFn: ({ signal }) =>
      api.get<VehicleListResponse>(`/v1/vehicles${buildQuery(filters)}`, { signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Paginated list query — feeds the `Načíst dalších N` button at the bottom of
 * the vehicles table. Each page loads `pageSize` rows; the next page's offset
 * is the count already loaded.
 */
export function useVehiclesInfiniteQuery(
  filters: Omit<VehicleListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['vehicles', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<VehicleListResponse>(
        `/v1/vehicles${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.vehicles.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

export interface Brand {
  id: number;
  nazev: string;
}

interface BrandsResponse {
  brands: Brand[];
}

export function useBrandsQuery() {
  return useQuery({
    queryKey: ['vehicles', 'brands'],
    queryFn: ({ signal }) => api.get<BrandsResponse>('/v1/vehicles/brands', { signal }),
  });
}

export interface VehicleDetail extends Vehicle {
  contract_id?: number | null;
  contract_status?: string | null;
  prepared_contract_id?: number | null;
  closed_contract_id?: number | null;
  /** All linked Premier zakázka numbers, comma-joined (migration 0017). */
  cisla_zakazek?: string | null;
  /** Server-computed: the standard equipment can still be pulled from LMS stock. */
  lze_nacist_lms_vybavu?: boolean;
}

interface VehicleDetailResponse {
  vehicle: VehicleDetail;
}

export const vehicleDetailKey = (id: number | null) => ['vehicles', 'detail', id] as const;

export function useVehicleDetailQuery(id: number | null) {
  return useQuery({
    queryKey: vehicleDetailKey(id),
    enabled: id !== null,
    queryFn: ({ signal }) =>
      api.get<VehicleDetailResponse>(`/v1/vehicles/${id}`, { signal }),
  });
}

export interface EquipmentItem {
  id: number;
  nazev: string;
  doekod?: string;
  cena?: string;
  id_dph?: number;
}

interface EquipmentResponse {
  equipment: EquipmentItem[];
}

/** STK / servisní prohlídka deadlines of the registered new cars — same shape as the ojeté ones. */
export function useVozidlaTerminyQuery(
  dny = 30,
  opts: { enabled?: boolean; rozsah?: 'aktivni' | 'skupina' } = {},
) {
  const rozsah = opts.rozsah ?? 'aktivni';
  return useQuery({
    queryKey: ['vehicles', 'terminy', dny, rozsah],
    enabled: opts.enabled ?? true,
    queryFn: ({ signal }) =>
      api.get<OjetaTerminyResponse>(`/v1/vehicles/terminy?dny=${dny}&rozsah=${rozsah}`, { signal }),
  });
}

export function useVehicleEquipmentQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'equipment', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      api.get<EquipmentResponse>(`/v1/vehicles/${id}/equipment`, { signal }),
  });
}

export interface VehicleEquipmentInput {
  doekod?: string;
  nazev: string;
  cena: string;
  id_dph?: number;
}

/**
 * Save vehicle equipment (replace all) — mirrors the nabídka/smlouva equipment
 * editors, but the `vozidlo_vybava` table is a single flat list (code + name +
 * price + VAT), so there's no original/neoriginal split here. Wires the edit
 * form's equipment section to the long-existing PUT endpoint the React UI had
 * never called.
 */
export function useSaveVehicleEquipmentMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { equipment: VehicleEquipmentInput[] }) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/${id}/equipment`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'equipment', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'detail', id] });
    },
  });
}

export interface ChargeEntry {
  id_nabijecky: number;
  id_vozidla: number;
  datum_nabiti: string;
  uroven_baterie: number;
  poznamka: string | null;
  id_prodejce: number | null;
  created_at: string | null;
  prodejce_jmeno?: string | null;
  prodejce_prijmeni?: string | null;
}

interface ChargesResponse {
  charges: ChargeEntry[];
}

export function useVehicleChargesQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'charges', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      api.get<ChargesResponse>(`/v1/vehicles/${id}/battery`, { signal }),
    retry: (failureCount, error) => {
      // 404 = vehicle has no battery records yet — don't retry, treat as empty.
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) {
        return false;
      }
      return failureCount < 1;
    },
  });
}

export interface AddChargeInput {
  datum_nabiti: string;
  uroven_baterie: number;
  poznamka?: string;
}

/**
 * Record an EV charge — the "Zaznamenat nabití" form
 * (POST /v1/vehicles/{id}/charge).
 * Invalidates both the charge history and the detail query, whose battery
 * summary derives from the newest charge.
 */
export function useAddChargeMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddChargeInput) =>
      api.post<{ status: 'ok' }>(`/v1/vehicles/${id}/charge`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'charges', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'detail', id] });
    },
  });
}

export interface ModifiedVehicle {
  id_vozidla: number;
  id_firmy: number;
  vc_auta: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  motor: string;
  barva: string;
  vin: string;
  stav_vozidla: string;
  prodejni_cena_navrh: string;
  znacka_nazev?: string | null;
  nazev_modelu?: string | null;
}

interface ModifiedVehiclesResponse {
  vehicles: ModifiedVehicle[];
}

export function useModifiedVehiclesQuery() {
  return useQuery({
    queryKey: ['vehicles', 'modified'],
    queryFn: ({ signal }) =>
      api.get<ModifiedVehiclesResponse>('/v1/vehicles/modified', { signal }),
  });
}

export function useResolveModifiedVehicleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/vehicles/modified/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'modified'] });
    },
  });
}

// ─── Vehicle create / update (Phase 11.6.3) ─────────────────────────────────
//
// Direct `/v1/vehicles` writes — `POST /` for new records (returns `{ id }`)
// and `PUT /{id}` for edits. Both endpoints accept any subset of the field
// inventory in `vozidlo` ([api/src/repositories/vehicle.repository.ts:151](../../../../api/src/repositories/vehicle.repository.ts)).
// The form keeps a strict TypeScript shape so undefined/blank values are not
// sent, mirroring the create handler's `?? ''` defaults.

export interface VehicleWriteBody {
  spz_vozu?: string;
  vc_auta?: string;
  znacka_vozu?: number;
  vozidlo_model?: number;
  provedeni?: string;
  verze?: string;
  motor?: string;
  barva?: string;
  prevodovka?: string;
  calouneni?: string;
  vin?: string;
  cislo_skladove_karty?: string;
  cislo_faktury?: number;
  hodnota_faktury?: string;
  datum_faktury?: string;
  umisteni?: string;
  stav_vozidla?: string;
  cenikova_cena?: string;
  nakupni_cena?: string;
  fleet_zaloha?: number;
  fleet_predpoklad?: number;
  fleet_skutecnost?: number;
  marketing_predpoklad?: number;
  prodejni_cena_navrh?: string;
  datum_predani?: string;
  prvni_registrace?: string;
  stk_platnost?: string;
  serviska?: string;
  poznamka?: string;
  vyplaceno_fmc?: string;
  cislo_zakazky?: string;
  rezervace?: string;
  id_zakaznika?: string;
  id_leasingovky?: number;
  modelovy_rok?: string;
}

export function useCreateVehicleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VehicleWriteBody) =>
      api.post<{ id: number }>('/v1/vehicles', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

export function useUpdateVehicleMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VehicleWriteBody) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      // A VIN edit moves the car's rental fleet entry with it (stock-exit hook).
      queryClient.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    },
  });
}

/**
 * Assign a customer to a vehicle card. PUT /v1/vehicles/{id}/assign-customer — the body
 * schema requires a positive id, so there is no unassign variant here.
 */
export function useAssignVehicleCustomerMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/${id}/assign-customer`, { id_zakaznika }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

/**
 * Duplicate a vehicle ("Duplikovat"). Server
 * deep-copies the vozidlo row + its equipment and returns the new id; the
 * caller navigates to the fresh detail. Optional `id_cilove_firmy` copies into
 * another dealership (cross-company duplicate); omit it for a local copy.
 */
export function useCopyVehicleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, id_cilove_firmy }: { id: number; id_cilove_firmy?: number }) =>
      api.post<{ id: number }>(
        `/v1/vehicles/${id}/copy`,
        id_cilove_firmy ? { id_cilove_firmy } : {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

/**
 * Duplicate a new vehicle into the used-vehicle inventory ("Duplikovat do
 * ojetých"). Server copies the row into the
 * ojeté table and returns its new id; the caller navigates to the ojeté detail.
 * Invalidates both inventories so the copy shows up under Ojetá vozidla.
 */
export function useCopyVehicleToUsedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ id: number }>(`/v1/vehicles/${id}/copy-to-used`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['ojeta'] });
      // The rental fleet entry follows the car to the used-car row.
      queryClient.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    },
  });
}

// ─── Vehicle catalog creation (brand-routed new-vehicle wizard) ─────────────
//
// Brand-driven creation paths (Ford LMS / ceník). Each returns the new vehicle
// id so the wizard can hand off to the equipment step / edit form.

export function useCreateVehicleFromCenikyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id_znacky: number;
      id_modelu: number;
      id_entity: number;
      id_interieru: number;
      id_barvy: number;
      id_vybavy?: number[];
    }) => api.post<{ id_vozidla: number }>('/v1/vehicles/create-from-ceniky', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

export function useCreateVehicleFromLmsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      entita: string;
      model_id: number;
      cenik_s_dph?: boolean;
      modelovy_rok?: string;
    }) => api.post<{ id_vozidla: number }>('/v1/vehicles/create-from-lms', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

export function useSaveVehicleLmsEquipmentMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LmsEquipmentSaveBody) =>
      api.post<Record<string, unknown>>(`/v1/vehicles/${id}/save-lms-equipment`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

/**
 * "Načíst výbavu z LMS": the server finds the car in the current LMS stock and stores its standard
 * equipment (on the car and its contracts that have none). Offered only while
 * `lze_nacist_lms_vybavu` is set on the detail.
 */
export function useNacistLmsVybavuMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ status: 'ok' }>(`/v1/vehicles/${id}/lms-vybava`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

// ─── Nabídky (Phase 2.4) ────────────────────────────────────────────────────

export interface NabidkaListItem {
  id_nabidky: number;
  id_zakaznika: number;
  id_uzivatele?: number | null;
  id_firmy?: number | null;
  id_vozidla?: number | null;
  znacka_vozu?: number | null;
  vozidlo_model?: number | null;
  provedeni?: string | null;
  verze?: string | null;
  barva?: string | null;
  motor?: string | null;
  prevodovka?: string | null;
  calouneni?: string | null;
  cena_celkem?: string | null;
  datum_nabidky?: string | null;
  vc_auta?: string | null;
  cislo_zakazky?: string | null;
  jmeno?: string | null;
  jmeno_uzivatele?: string | null;
  text_interni_poznamka?: string | null;
  nazev_znacky?: string | null;
  nazev_modelu?: string | null;
  pocet_priloh?: number | null;
}

interface NabidkyListResponse {
  nabidky: NabidkaListItem[];
  total: number;
}

export interface NabidkyListFilters {
  hledanejmeno?: string;
  cislo_nabidky?: string;
  cislo_zakazky?: string;
  limit?: string;
  offset?: string;
}

export function useNabidkyQuery(filters: NabidkyListFilters) {
  return useQuery({
    queryKey: ['vehicles', 'nabidky', filters],
    queryFn: ({ signal }) =>
      api.get<NabidkyListResponse>(`/v1/vehicles/nabidky${buildQuery(filters)}`, { signal }),
    placeholderData: (previous) => previous,
  });
}

export function useNabidkyInfiniteQuery(
  filters: Omit<NabidkyListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['vehicles', 'nabidky', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<NabidkyListResponse>(
        `/v1/vehicles/nabidky${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.nabidky.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

export interface NabidkaDetail {
  id_nabidky: number;
  id_zakaznika: number;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  barva: string;
  motor: string;
  prevodovka: string;
  calouneni: string;
  zakladni_cena: string;
  sleva_zakladni_cena: string;
  cena_celkem: string;
  datum_uzavreni: string;
  datum_predani: string;
  typ_financovani: number;
  misto_predani: number;
  id_firmy: number;
  id_uzivatele: number;
  id_vozidla: number;
  id_dph: number;
  datum_nabidky: string | null;
  lms_entita_json: string | null;
  lms_vybava_json: string | null;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  vc_auta: string | null;
  cislo_zakazky: string | null;
  pocet_priloh: number | null;
}

interface NabidkaDetailResponse {
  nabidka: NabidkaDetail;
}

export function useNabidkaDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'nabidky', 'detail', id],
    enabled: id !== null,
    // Backs the wizard's edit steps (Vozidlo / Sleva / Financování seed their
    // form state from this). The offer detail page observes only the summary
    // query, so a save there invalidates this query without refetching it —
    // forcing a mount-time refetch guarantees the edit pages open against
    // fresh server state instead of a stale cache.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<NabidkaDetailResponse>(`/v1/vehicles/nabidky/${id}`, { signal }),
  });
}

export interface NabidkaSummaryCompany {
  Nazev?: string | null;
  ulice?: string | null;
  cp?: string | null;
  mesto?: string | null;
  psc?: string | null;
  ICO?: number | null;
  DIC?: string | null;
  zapis_OR?: string | null;
  banka?: string | null;
  ucet?: string | null;
  /** Days the offer stays valid — company setting, default 7. */
  nabidka_platnost_dni?: number;
}

export interface NabidkaSummarySeller {
  jmeno?: string | null;
  prijmeni?: string | null;
}

export interface NabidkaVatRate {
  id_dph: number;
  hodnota: number;
  nazev_zkratka: string;
}

export interface NabidkaEquipmentItem {
  id: number;
  id_nabidky: number;
  doekod?: string;
  nazev: string;
  cena: string;
  id_dph: number;
  hodnota_dph?: number | null;
  nazev_dph?: string | null;
}

export interface NabidkaAttachment {
  id_prilohy: number;
  nazev: string;
  poznamka: string | null;
  id_dokumentu: number;
  klic: string;
}

export interface NabidkaSummaryResponse {
  nabidka: NabidkaDetail & { hodnota_dph?: number | null; nazev_dph?: string | null };
  company: NabidkaSummaryCompany | null;
  seller: NabidkaSummarySeller | null;
  vatRates: NabidkaVatRate[];
  equipment: {
    original: NabidkaEquipmentItem[];
    neoriginal: NabidkaEquipmentItem[];
  };
  deliveryPlace: { id_mista: number; nazev?: string | null; ulice?: string | null; psc?: string | null; mesto?: string | null } | null;
  financingType: { id_financovani: number; [k: string]: unknown } | null;
  dodatek: { obsah?: string | null; [k: string]: unknown } | null;
  attachments: NabidkaAttachment[];
  cena_celkem: number | null;
  /** Contracts made from this offer. */
  smlouvy: Array<{ id_smlouvy: number; stav_smlouvy: string }>;
}

export function useNabidkaSummaryQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'nabidky', 'summary', id],
    enabled: id !== null,
    // Drives the offer detail page and the wizard's Sleva live preview — both
    // are "fresh data" surfaces, so always refetch on mount rather than serve a
    // possibly-stale cache.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<NabidkaSummaryResponse>(`/v1/vehicles/nabidky/${id}/summary`, { signal }),
  });
}

/**
 * Update mutation widened in Phase 11.6.1.2 to expose every vehicle-field
 * `UpdateNabidkaBodySchema` accepts — the wizard's Vozidlo step needs to
 * write znacka/model/provedeni/etc. through the offer update endpoint.
 */
export interface UpdateNabidkaBody {
  text_interni_poznamka?: string;
  dodatecne_informace?: string;
  typ_financovani?: number | null;
  misto_predani?: number | null;
  datum_predani?: string;
  datum_uzavreni?: string;
  id_vozidla?: number;
  znacka_vozu?: number;
  vozidlo_model?: number;
  provedeni?: string;
  verze?: string;
  barva?: string;
  motor?: string;
  prevodovka?: string;
  calouneni?: string;
  zakladni_cena?: number;
  id_dph?: number;
}

export function useUpdateNabidkaMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateNabidkaBody) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/nabidky/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

export interface NabidkaCompany {
  id_firmy: number;
  Nazev: string;
}

export interface NabidkaCompaniesResponse {
  companies: NabidkaCompany[];
  currentCompanyId: number;
}

/**
 * Companies the current user may copy an offer into. Drives the duplicate/copy
 * menu: the entry matching `currentCompanyId` is an in-place duplicate, the
 * rest are cross-company copies.
 */
export function useNabidkaCompaniesQuery(enabled = true) {
  return useQuery({
    queryKey: ['vehicles', 'nabidky', 'companies'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<NabidkaCompaniesResponse>('/v1/vehicles/nabidky/companies', { signal }),
  });
}

/**
 * Duplicate an offer (legacy "Duplikovat nabídku"). Server deep-copies the
 * nabídka + its equipment rows and returns the new id; the caller navigates to
 * the fresh detail. Invalidates the list so the copy shows up immediately.
 * Optional `id_cilove_firmy` copies into another dealership (cross-company
 * copy — drops the internal note); omit it for an in-place duplicate.
 */
export function useCopyNabidkaMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, id_cilove_firmy }: { id: number; id_cilove_firmy?: number }) =>
      api.post<{ id: number }>(
        `/v1/vehicles/nabidky/${id}/copy`,
        id_cilove_firmy ? { id_cilove_firmy } : {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

export function useAssignNabidkaCustomerMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/nabidky/${id}/customer`, { id_zakaznika }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

export function useSetNabidkaDiscountMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { sleva_procenta?: number | null; sleva_bez_dph?: number | null }) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/nabidky/${id}/discount`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'summary', id] });
      // Discount changes shift cena_celkem on the nabidka row, which the
      // wizard's Crown reads from the detail query — invalidate it too so the
      // header doesn't lag behind the recap.
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'detail', id] });
    },
  });
}

export function useConvertNabidkaToContractMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ id_smlouvy: number; status?: string }>(
        `/v1/vehicles/nabidky/${id}/convert-to-contract`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy'] });
    },
  });
}

// ─── Nabídka create hooks (Phase 11.6.1.2) ──────────────────────────────────
//
// Four creation paths. All four return `{ id_nabidky }` which the wizard uses to navigate
// to `?step=vybava&id=…`.

export function useCreateNabidkaManualMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id_znacky?: number;
      id_modelu?: number;
      cena?: number;
      provedeni?: string;
      verze?: string;
      barva?: string;
      motor?: string;
      prevodovka?: string;
      calouneni?: string;
    }) =>
      api.post<{ id_nabidky: number }>('/v1/vehicles/nabidky/create-manual', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

export function useCreateNabidkaFromCenikyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id_znacky: number;
      id_modelu: number;
      id_entity: number;
      id_interieru: number;
      id_barvy: number;
      id_dph?: number;
      id_vybavy?: number[];
    }) =>
      api.post<{ id_nabidky: number }>('/v1/vehicles/nabidky/create-from-ceniky', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

export function useCreateNabidkaFromLmsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { entita: string; model_id: number; cenik_s_dph?: boolean }) =>
      api.post<{ id_nabidky: number }>('/v1/vehicles/nabidky/create-from-lms', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

export function useCreateNabidkaFromVehicleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id_vozidla: number; id_zakaznika?: number }) =>
      api.post<{ id_nabidky: number }>('/v1/vehicles/nabidky/create-from-vehicle', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

// ─── Nabídka equipment (Phase 11.6.1.2) ─────────────────────────────────────

interface NabidkaEquipmentResponse {
  original: NabidkaEquipmentItem[];
  neoriginal: NabidkaEquipmentItem[];
}

export function useNabidkaEquipmentQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'nabidky', 'equipment', id],
    enabled: id !== null,
    // Backs the wizard's Výbava edit step. The detail page never observes this
    // query, so it isn't refetched after a save there — force a mount-time
    // refetch so the editor always opens against fresh server state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<NabidkaEquipmentResponse>(`/v1/vehicles/nabidky/${id}/equipment`, { signal }),
  });
}

export interface NabidkaOriginalEquipmentInput {
  doekod?: string;
  nazev: string;
  cena: string;
  id_dph?: number;
}

export interface NabidkaNonOriginalEquipmentInput {
  nazev: string;
  cena: string;
  id_dph?: number;
}

export function useSaveNabidkaEquipmentMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      original?: NabidkaOriginalEquipmentInput[];
      neoriginal?: NabidkaNonOriginalEquipmentInput[];
    }) => api.put<{ status: 'ok' }>(`/v1/vehicles/nabidky/${id}/equipment`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'equipment', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'summary', id] });
      // Equipment edits shift cena_celkem on the nabidka row (recalculateTotal
      // persists it server-side), so the wizard Crown's detail-query cache
      // would otherwise lag behind the recap with a stale total.
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'detail', id] });
    },
  });
}

/**
 * Ford LMS equipment save. Persists the chosen
 * colour / upholstery / paid-extras against the offer; the server recomputes
 * the total, so we invalidate detail + summary like the manual editor does.
 */
export function useSaveNabidkaLmsEquipmentMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LmsEquipmentSaveBody) =>
      api.post<Record<string, unknown>>(`/v1/vehicles/nabidky/${id}/save-lms-equipment`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'equipment', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'nabidky'] });
    },
  });
}

/**
 * Models for a brand — same endpoint the vehicle list filter uses
 * (`GET /v1/vehicles/models/{brandId}`). Keeps the wizard's Vozidlo step in
 * sync with the rest of the workspace.
 */
export interface VehicleModel {
  id: number;
  nazev_modelu: string;
}

export function useVehicleModelsQuery(brandId: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'models', brandId],
    enabled: brandId != null && Number.isFinite(brandId) && brandId > 0,
    queryFn: ({ signal }) =>
      api.get<{ models: VehicleModel[] }>(`/v1/vehicles/models/${brandId}`, { signal }),
  });
}

// ─── Ceník entities / colors / interiors (Phase 11.6.1.4) ───────────────────
//
// Wires the wizard's Vozidlo step ceník branch
// (brands 6/11) and feeds the `create-from-ceniky` mutation.

export interface VehicleEntity {
  id: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  motor: string;
  prevodovka: string;
  cenikova_cena: number;
  nakupni_cena: number;
  aktivni: number;
}

export function useVehicleEntitiesQuery(modelId: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'entities', modelId],
    enabled: modelId != null && Number.isFinite(modelId) && modelId > 0,
    queryFn: ({ signal }) =>
      api.get<{ entities: VehicleEntity[] }>(`/v1/vehicles/entities/${modelId}`, { signal }),
  });
}

export interface EntityColor {
  id: number;
  id_entity: number;
  nazev: string;
  cenikova_cena: number;
  nakupni_cena: number;
  aktivni: number;
}

export function useEntityColorsQuery(entityId: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'entity-colors', entityId],
    enabled: entityId != null && Number.isFinite(entityId) && entityId > 0,
    queryFn: ({ signal }) =>
      api.get<{ colors: EntityColor[] }>(`/v1/vehicles/entity/${entityId}/colors`, { signal }),
  });
}

export interface EntityInterior {
  id: number;
  id_entity: number;
  nazev: string;
  cenikova_cena: number;
  nakupni_cena: number;
  aktivni: number;
}

export function useEntityInteriorQuery(entityId: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'entity-interior', entityId],
    enabled: entityId != null && Number.isFinite(entityId) && entityId > 0,
    queryFn: ({ signal }) =>
      api.get<{ interior: EntityInterior[] }>(`/v1/vehicles/entity/${entityId}/interior`, { signal }),
  });
}

export interface EntityEquipment {
  id: number;
  id_entity: number;
  nazev: string;
  cenikova_cena: number;
  nakupni_cena: number;
  aktivni: number;
}

export function useEntityEquipmentQuery(entityId: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'entity-equipment', entityId],
    enabled: entityId != null && Number.isFinite(entityId) && entityId > 0,
    queryFn: ({ signal }) =>
      api.get<{ equipment: EntityEquipment[] }>(`/v1/vehicles/entity/${entityId}/equipment`, { signal }),
  });
}

// VAT rates — shared reference endpoint; ojeta-vozidla ships its own copy.
// Inlined here so the vozidla wizard doesn't cross-module-import.

export interface VatRate {
  id_dph: number;
  hodnota: number;
  nazev: string;
  nazev_zkratka: string;
}

export function useVatRatesQuery() {
  return useQuery({
    queryKey: ['reference', 'vat-rates'],
    staleTime: 60 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<{ rates: VatRate[] }>('/v1/reference/vat-rates', { signal }),
  });
}

// Financing types + delivery places — shared reference endpoints; ojeta-vozidla
// ships its own copies. Inlined here per the 11.6.2.2 calibration ("the wizard
// avoids cross-module-importing siblings"), so the smlouva wizard's Financování
// step (Phase 11.6.2.6) gets its own hooks against the matching endpoints.

export interface DeliveryPlace {
  id_mista: number;
  nazev: string;
  ulice: string;
  psc: string;
  mesto: string;
}

export interface FinancingType {
  id_financovani: number;
  nazev: string;
}

export function useDeliveryPlacesQuery() {
  return useQuery({
    queryKey: ['reference', 'delivery-places'],
    staleTime: 60 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<{ places: DeliveryPlace[] }>('/v1/reference/delivery-places', { signal }),
  });
}

export function useFinancingTypesQuery() {
  return useQuery({
    queryKey: ['reference', 'financing-types'],
    staleTime: 60 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<{ types: FinancingType[] }>('/v1/reference/financing-types', { signal }),
  });
}

// ─── Smlouvy (Phase 2.5) ────────────────────────────────────────────────────

export interface SmlouvaListItem {
  id_smlouvy: number | null;
  id_zakaznika: number | null;
  jmeno: string | null;
  znacka_vozu: number | null;
  stav_smlouvy?: string | null;
  vcauta?: string | null;
  id_vozidla?: number | null;
  provedeni?: string | null;
  verze?: string | null;
  barva?: string | null;
  motor?: string | null;
  prevodovka?: string | null;
  calouneni?: string | null;
  cena_celkem?: string | null;
  id_uzivatele?: number | null;
  id_dph?: number | null;
  jmeno_uzivatele?: string | null;
  text_interni_poznamka?: string | null;
  cislo_zakazky?: string | null;
  pocet_priloh?: number | null;
  datum_uzavreni?: string | null;
  predavaci_protokol?: string | null;
  vin?: string | null;
  datum_predani?: string | null;
  nazev_znacky?: string | null;
  nazev_modelu?: string | null;
  vozidlo_cislo_faktury?: number | null;
  vozidlo_hodnota_faktury?: string | null;
}

interface SmluvyListResponse {
  smlouvy: SmlouvaListItem[];
  total: number;
  closed_count?: number;
  with_protocol_count?: number;
}

export interface SmluvyListFilters {
  hledanejmeno?: string;
  cislo_smlouvy?: string;
  cislo_zakazky?: string;
  limit?: string;
  offset?: string;
}

export function useSmluvyQuery(filters: SmluvyListFilters) {
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', filters],
    queryFn: ({ signal }) =>
      api.get<SmluvyListResponse>(`/v1/vehicles/smlouvy${buildQuery(filters)}`, { signal }),
    placeholderData: (previous) => previous,
  });
}

export function useSmluvyInfiniteQuery(
  filters: Omit<SmluvyListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['vehicles', 'smlouvy', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<SmluvyListResponse>(
        `/v1/vehicles/smlouvy${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.smlouvy.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

export interface SmlouvaDetail {
  id_smlouvy: number;
  id_zakaznika: number;
  stav_smlouvy: string;
  vcauta: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  barva: string;
  motor: string;
  prevodovka: string;
  calouneni: string;
  zakladni_cena: string;
  sleva_zakladni_cena: string;
  cena_celkem: string;
  datum_uzavreni: string;
  datum_predani: string;
  typ_financovani: number;
  misto_predani: number;
  predavaci_protokol: string;
  id_firmy: number;
  id_uzivatele: number;
  id_vozidla: number;
  id_dph: number;
  /** Offer this contract was made from (null = none / deleted). */
  id_nabidky: number | null;
  lms_entita_json: string | null;
  lms_vybava_json: string | null;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  hodnota_dph: number | null;
  nazev_dph: string | null;
  cislo_zakazky?: string | null;
  jmeno_zakaznika?: string | null;
  vozidlo_cislo_faktury?: string | null;
  vozidlo_hodnota_faktury?: string | null;
  vozidlo_fleet_zaloha?: number | null;
  vozidlo_fleet_skutecnost?: number | null;
  vozidlo_fleet_predpoklad?: number | null;
  vozidlo_marketing_predpoklad?: number | null;
  vozidlo_cenikova_cena?: string | null;
}

export interface SmlouvaCost {
  id_smlouvy: number;
  druh_nakladu: string;
  castka: string;
}

export interface SmlouvaProtocolStub {
  vin?: string | null;
  registracni_znacka?: string | null;
  cislo_objednavky?: string | null;
}

interface SmlouvaDetailResponse {
  smlouva: SmlouvaDetail;
}

export function useSmlouvaDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', 'detail', id],
    enabled: id !== null,
    // Backs the contract wizard's edit steps + the protokol editor. The
    // contract detail page observes only the summary query, so this query is
    // invalidated-but-unobserved after a save there — force a mount-time
    // refetch so the editors open against fresh server state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SmlouvaDetailResponse>(`/v1/vehicles/smlouvy/${id}`, { signal }),
  });
}

export interface SmlouvaSummaryResponse {
  smlouva: SmlouvaDetail;
  company: NabidkaSummaryCompany | null;
  seller: NabidkaSummarySeller | null;
  vatRates: NabidkaVatRate[];
  equipment: {
    original: NabidkaEquipmentItem[];
    neoriginal: NabidkaEquipmentItem[];
  };
  deliveryPlace: { id_mista: number; nazev?: string | null; ulice?: string | null; psc?: string | null; mesto?: string | null } | null;
  financingType: { id_financovani: number; [k: string]: unknown } | null;
  dodatek: { dodatecne_informace?: string | null; [k: string]: unknown } | null;
  attachments: NabidkaAttachment[];
  cena_celkem: number | null;
  costs?: SmlouvaCost[];
  protocol?: SmlouvaProtocolStub | null;
}

export function useSmlouvaSummaryQuery(
  id: number | null,
  options?: { pronajimatelOverride?: number },
) {
  // `pronajimatel_override` is documented at api/src/routes/vehicles.ts —
  // only the leasing-print variant uses it (id_firmy=7001) to swap the
  // `company` slice for a fixed landlord. Keep it nullable so the existing
  // non-leasing callers don't change shape.
  const override = options?.pronajimatelOverride;
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', 'summary', id, override ?? null],
    enabled: id !== null,
    // Drives the contract detail page and the wizard's Sleva live preview —
    // both want fresh data on entry, so always refetch on mount.
    refetchOnMount: 'always',
    queryFn: ({ signal }) => {
      const qs = override ? `?pronajimatel_override=${override}` : '';
      return api.get<SmlouvaSummaryResponse>(
        `/v1/vehicles/smlouvy/${id}/summary${qs}`,
        { signal },
      );
    },
  });
}

export interface HandoverProtocol {
  id_protokolu: number;
  id_smlouvy: number;
  datum: string;
  technicky_prukaz: string;
  pocet_klicu: string;
  kod_barvy: string;
  vin: string;
  registracni_znacka: string;
  cislo_objednavky: string;
  servis_mesice: string;
  servis_km: string;
  odhad_servisu: string;
}

interface HandoverProtocolResponse {
  protocol: HandoverProtocol | null;
}

export function useHandoverProtocolQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', 'protocol', id],
    enabled: id !== null,
    // Backs the protokol editor — must open against fresh server state, so
    // always refetch on mount rather than serve a possibly-stale cache.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<HandoverProtocolResponse>(`/v1/vehicles/smlouvy/${id}/handover-protocol`, { signal }),
  });
}

/**
 * `POST /v1/vehicles/smlouvy/{id}/close` body. A contract may close while already
 * carrying a vehicle (empty body), by connecting an existing stock vehicle
 * (`id_vozidla`), or explicitly with no vehicle at all (`bez_vozidla`).
 * `rezervace_potvrzena: 'Ano'` overrides a reservation conflict.
 */
export interface CloseSmlouvaBody {
  id_vozidla?: number;
  bez_vozidla?: boolean;
  rezervace_potvrzena?: 'Ano';
  datum_uzavreni?: string;
}

export interface CloseSmlouvaResult {
  status: string;
  message?: string;
  vehicleId?: number;
}

export function useCloseSmlouvaMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CloseSmlouvaBody = {}) =>
      api.post<CloseSmlouvaResult>(`/v1/vehicles/smlouvy/${id}/close`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
    },
  });
}

/**
 * `GET /v1/vehicles/smlouvy/{id}/available-vehicles` — stock/registered
 * vehicles matching the contract's model, offered when closing a contract that
 * has no vehicle connected (legacy `vyhledat_vozidla()`).
 */
export function useAvailableVehiclesForSmlouvaQuery(smlouvaId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', 'available-vehicles', smlouvaId],
    enabled: enabled && Number.isFinite(smlouvaId),
    queryFn: ({ signal }) =>
      api.get<{ vehicles: Vehicle[]; total: number }>(
        `/v1/vehicles/smlouvy/${smlouvaId}/available-vehicles`,
        { signal },
      ),
  });
}

/**
 * `POST /v1/vehicles/smlouvy/{id}/close-create-vehicle` — closes the contract
 * and spawns a new vehicle card pre-filled from the contract's specs (legacy
 * `vytvorit_vyplnene_vozidlo()`).
 */
export function useCloseSmlouvaCreateVehicleMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { datum_uzavreni?: string } = {}) =>
      api.post<{ status: 'ok'; vehicleId: number; message: string }>(
        `/v1/vehicles/smlouvy/${id}/close-create-vehicle`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'list'] });
    },
  });
}

// ─── Smlouva wizard write-side (Phase 11.6.2.2) ─────────────────────────────
//
// Mirrors the nabídka wizard hooks (Phase 11.6.1.2) against the smlouvy
// endpoints. Pinned by `Phase 11.6.2.1: smlouva wizard write-side` tests in
// [api/tests/vozidla-smlouvy.test.ts](../../../../api/tests/vozidla-smlouvy.test.ts).

/**
 * `PUT /v1/vehicles/smlouvy/{id}` body — every field the wizard's Vozidlo +
 * Financování steps need to write through a single endpoint. Schema-side
 * counterpart: `UpdateSmlouvaBodySchema` ([api/src/schemas/vehicles.ts](../../../../api/src/schemas/vehicles.ts)).
 */
export interface UpdateSmlouvaBody {
  text_interni_poznamka?: string;
  typ_financovani?: number;
  misto_predani?: number;
  datum_predani?: string;
  datum_uzavreni?: string;
  vcauta?: string;
  znacka_vozu?: number;
  vozidlo_model?: number;
  provedeni?: string;
  verze?: string;
  barva?: string;
  motor?: string;
  prevodovka?: string;
  calouneni?: string;
  zakladni_cena?: number;
  id_dph?: number;
}

export function useUpdateSmlouvaMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSmlouvaBody) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/smlouvy/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy'] });
    },
  });
}

/**
 * Copy a contract. The
 * `CopySmlouvaBody` takes an optional target company + a customer in that
 * company the copy binds to — required for cross-company copies (otherwise
 * the server resets `id_zakaznika` to 0). Omit both for a plain in-place
 * duplicate. Returns the new contract id as `{ id_smlouvy }`.
 */
export function useCopySmlouvaMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      id_cilove_firmy,
      id_zakaznika,
    }: {
      id: number;
      id_cilove_firmy?: number;
      id_zakaznika?: number;
    }) =>
      api.post<{ id_smlouvy: number }>(`/v1/vehicles/smlouvy/${id}/copy`, {
        ...(id_cilove_firmy != null ? { id_cilove_firmy } : {}),
        ...(id_zakaznika != null ? { id_zakaznika } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy'] });
    },
  });
}

/**
 * `GET /v1/vehicles/smlouvy/{id}/costs` — contract costs/expenses rows
 * (`vozidla_smlouva_naklady`) feeding the vyhodnocení margin breakdown.
 * Shape pinned by `SmlouvaCostsResponseSchema` in api/src/schemas/vehicles.ts.
 */
export interface SmlouvaCostRow {
  id: number;
  id_smlouvy: number;
  druh_nakladu: string;
  castka: number;
}

export interface SmlouvaCostsResponse {
  costs: SmlouvaCostRow[];
}

export function useSmlouvaCostsQuery(id: number | null, enabled = true) {
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', 'costs', id],
    enabled: enabled && id !== null && Number.isFinite(id),
    // The costs editor mounts on demand (modal) — always open against fresh
    // server state, same as the other smlouva editors.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SmlouvaCostsResponse>(`/v1/vehicles/smlouvy/${id}/costs`, { signal }),
  });
}

/**
 * `PUT /v1/vehicles/smlouvy/{id}/costs` — batch replace of the costs list
 * (`SaveSmlouvaCostsBodySchema`: druh_nakladu max 50, castka number). Always
 * resubmits the full edited list.
 */
export interface SaveSmlouvaCostsBody {
  costs: Array<{ druh_nakladu: string; castka: number }>;
}

export function useSaveSmlouvaCostsMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveSmlouvaCostsBody) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/smlouvy/${id}/costs`, body),
    onSuccess: () => {
      // The summary response embeds `costs`, so invalidate it alongside the
      // costs query itself (same fan-out as useUpdateSmlouvaMutation).
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'costs', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
    },
  });
}

export function useAssignSmlouvaCustomerMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/smlouvy/${id}/customer`, { id_zakaznika }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy'] });
      // The contract's customer is mirrored onto the linked vehicle server-side.
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'detail'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'list'] });
    },
  });
}

export function useSetSmlouvaDiscountMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { sleva_procenta?: number | null; sleva_bez_dph?: number | null }) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/smlouvy/${id}/discount`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
    },
  });
}

/**
 * `POST /v1/vehicles/smlouvy/create` — only direct-create path. `id_vozidla`
 * is required (smlouvy must be created from a vehicle row OR via nabídka
 * conversion; see `Phase 11.6.2.1: POST /smlouvy/create is the only create
 * path` test for the constraint).
 */
export function useCreateSmlouvaFromVehicleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id_vozidla: number }) =>
      api.post<{ id_smlouvy: number }>('/v1/vehicles/smlouvy/create', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy'] });
    },
  });
}

// ─── Handover / Předat vozidlo (Phase 11.7.x) ──────────────────────────────
//
// Records the handover date and flips `stav_vozidla` to "Předaný zákazníkovi"
// or "Předaný dealerovi" (PUT /v1/vehicles/{id}/handover). For the dealer
// target the backend stores `kod_dealera` in `id_zakaznika`, matching legacy.
export interface VehicleHandoverBody {
  target: 'customer' | 'dealer';
  datum_predani: string;
  /** Required when target === 'dealer'. */
  kod_dealera?: string;
}

export function useVehicleHandoverMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VehicleHandoverBody) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/${id}/handover`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      // A handover takes the car out of the rental fleet (stock-exit hook).
      queryClient.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    },
  });
}

// ─── Smlouva equipment (Phase 11.6.2.2) ─────────────────────────────────────

export interface SmlouvaEquipmentOriginalItem {
  id: number;
  id_smlouvy: number;
  doekod: string;
  nazev: string;
  cena: string;
  id_dph: number;
}

export interface SmlouvaEquipmentNonOriginalItem {
  id: number;
  id_smlouvy: number;
  nazev: string;
  cena: string;
  id_dph: number;
}

interface SmlouvaEquipmentResponse {
  original: SmlouvaEquipmentOriginalItem[];
  neoriginal: SmlouvaEquipmentNonOriginalItem[];
}

export function useSmlouvaEquipmentQuery(id: number | null) {
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', 'equipment', id],
    enabled: id !== null,
    // Backs the contract wizard's Výbava edit step. The detail page never
    // observes this query, so force a mount-time refetch — otherwise the editor
    // opens against a stale cache and a save would clobber unseen changes.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SmlouvaEquipmentResponse>(`/v1/vehicles/smlouvy/${id}/equipment`, { signal }),
  });
}

export interface SmlouvaOriginalEquipmentInput {
  doekod?: string;
  nazev: string;
  cena: string;
  id_dph?: number;
}

export interface SmlouvaNonOriginalEquipmentInput {
  nazev: string;
  cena: string;
  id_dph?: number;
}

export function useSaveSmlouvaEquipmentMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      original?: SmlouvaOriginalEquipmentInput[];
      neoriginal?: SmlouvaNonOriginalEquipmentInput[];
    }) => api.put<{ status: 'ok' }>(`/v1/vehicles/smlouvy/${id}/equipment`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'equipment', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
    },
  });
}

// ─── Handover protocol write-side (Phase 11.6.2.2) ──────────────────────────
//
// POST creates (idempotent: server-side delete + re-insert), PUT updates.
// Both accept the same 10 fields — the form in Phase 11.6.2.7 picks one based
// on whether `useHandoverProtocolQuery(id).data.protocol` is null.

export interface HandoverProtocolBody {
  datum?: string;
  technicky_prukaz?: string;
  pocet_klicu?: string;
  kod_barvy?: string;
  vin?: string;
  registracni_znacka?: string;
  cislo_objednavky?: string;
  servis_mesice?: string;
  servis_km?: string;
  odhad_servisu?: string;
}

export function useCreateHandoverProtocolMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: HandoverProtocolBody) =>
      api.post<{ status: 'ok' }>(`/v1/vehicles/smlouvy/${id}/handover-protocol`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'protocol', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'detail', id] });
    },
  });
}

export function useUpdateHandoverProtocolMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: HandoverProtocolBody) =>
      api.put<{ status: 'ok' }>(`/v1/vehicles/smlouvy/${id}/handover-protocol`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'protocol', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary', id] });
    },
  });
}

// ─── Soubory / S3 (Phase 2.6) ───────────────────────────────────────────────

interface AttachmentsResponse {
  attachments: NabidkaAttachment[];
}

type AttachmentParent = 'nabidky' | 'smlouvy';

function attachmentBase(parent: AttachmentParent, id: number) {
  return `/v1/vehicles/${parent}/${id}/files`;
}

export function useAttachmentsQuery(parent: AttachmentParent, id: number | null) {
  return useQuery({
    queryKey: ['vehicles', parent, 'files', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      api.get<AttachmentsResponse>(attachmentBase(parent, id as number), { signal }),
  });
}

export function useUploadAttachmentMutation(parent: AttachmentParent, id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; nazev?: string; poznamka?: string }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      if (input.nazev) fd.append('nazev', input.nazev);
      if (input.poznamka) fd.append('poznamka', input.poznamka);
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...getAuthHeaders(),
      };
      const res = await fetch(`/api${attachmentBase(parent, id)}`, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      return (await res.json()) as { id: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', parent, 'files', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', parent, 'summary', id] });
    },
  });
}

export function useUpdateAttachmentMutation(parent: AttachmentParent, id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { fileId: number; nazev: string; poznamka?: string }) =>
      api.put<{ status: 'ok' }>(`${attachmentBase(parent, id)}/${input.fileId}`, {
        nazev: input.nazev,
        poznamka: input.poznamka ?? '',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', parent, 'files', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', parent, 'summary', id] });
    },
  });
}

export function useDeleteAttachmentMutation(parent: AttachmentParent, id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: number) =>
      api.delete<{ status: 'ok' }>(`${attachmentBase(parent, id)}/${fileId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', parent, 'files', id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', parent, 'summary', id] });
    },
  });
}

// ─── LMS sync (Phase 2.7) ───────────────────────────────────────────────────

/** New leads of one LMS location (dealer account); a lead belongs to its location. */
export interface LmsLeadsPobocka {
  /** firma_lms_pobocky.id — pass it to take-over. */
  id: number;
  nazev: string;
  lead_ids: number[];
  existing_ids: number[];
  new_ids: number[];
  /** not_configured = incomplete credentials; upstream = the LMS call failed. */
  error: 'not_configured' | 'upstream' | null;
}

export interface LmsNewLeadsResponse {
  /** Main location first. */
  pobocky: LmsLeadsPobocka[];
}

export function lmsPobockaErrorLabel(error: LmsLeadsPobocka['error']): string | null {
  if (error === 'not_configured') return 'Pobočka nemá nastavené LMS klíče.';
  if (error === 'upstream') return 'LMS odpověděl chybou.';
  return null;
}

export function useLmsNewLeadsQuery(datum: string | null) {
  return useQuery({
    enabled: !!datum,
    queryKey: ['lms', 'leads', 'new', datum],
    queryFn: ({ signal }) =>
      api.get<LmsNewLeadsResponse>(`/v1/lms/leads/new?datum=${encodeURIComponent(datum!)}`, {
        signal,
      }),
  });
}

export interface LmsTakeOverResponse {
  id_kontaktu: number;
  jmeno: string;
  lms_id: number;
}

export function useLmsTakeOverMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    // pobockaId = the location the lead was listed under; it signs the LMS
    // calls and is stored on the new contact for later close/reopen sync.
    mutationFn: ({ leadId, pobockaId }: { leadId: number; pobockaId: number }) =>
      api.post<LmsTakeOverResponse>(`/v1/lms/leads/${leadId}/take-over`, { pobocka_id: pobockaId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lms', 'leads', 'new'] });
    },
  });
}

export type LmsMasterdataKey =
  | 'actions'
  | 'dealerships'
  | 'disinterest-reasons'
  | 'salespeople'
  | 'lead-origins'
  | 'lead-sources'
  | 'vehicle-models';

/**
 * Masterdata GETs return the upstream LMS payload as-is (e.g. `{ akce: [...] }`,
 * `{ vozy: [...] }`). The backend doesn't extract the wrapper key — that
 * matches the legacy shims, which dumped the decoded JSON unchanged. This
 * hook surfaces the whole `payload` object so the UI can render it as-is.
 */
export interface LmsMasterdataResult {
  key: LmsMasterdataKey;
  payload: Record<string, unknown>;
}

export function useLmsMasterdataQuery(key: LmsMasterdataKey | null) {
  return useQuery<LmsMasterdataResult>({
    enabled: !!key,
    queryKey: ['lms', 'masterdata', key],
    queryFn: async ({ signal }) => {
      const k = key as LmsMasterdataKey;
      const json = await api.get<{ payload: Record<string, unknown> }>(
        `/v1/lms/masterdata/${k}`,
        { signal },
      );
      return { key: k, payload: json.payload ?? {} };
    },
  });
}

export interface LmsSyncResponse {
  inserted: number;
  total: number;
}

export function useLmsSyncDisinterestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<LmsSyncResponse>('/v1/lms/masterdata/disinterest-reasons/sync'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lms', 'masterdata', 'disinterest-reasons'] });
    },
  });
}

export function useLmsSyncVehicleModelsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<LmsSyncResponse>('/v1/lms/masterdata/vehicle-models/sync'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lms', 'masterdata', 'vehicle-models'] });
    },
  });
}

// ─── Ford LMS catalog cascade (new-vehicle + offer Ford branch) ─────────────
//
// The Ford brand creation path drives a cascade of HMAC-signed Fordissimo
// catalog reads, server-proxied through `POST /v1/vehicles/lms/*`:
//   models → model-years → pricelists → entities → equipment.
// The backend returns the upstream payload mostly as-is, so these interfaces capture the fields the UI reads and let
// the rest pass through. Shared by the offer wizard's Ford branch and the
// new-vehicle wizard so there's one cascade implementation.

export interface LmsModel {
  ID: number | string;
  Nazev: string;
  Foto?: string;
  [k: string]: unknown;
}
export interface LmsModelYear {
  KodModelu: string;
  NazevModelu: string;
  Rok: number | string;
  [k: string]: unknown;
}
export interface LmsPricelist {
  ID: number | string;
  NazevModelu: string;
  DatumVydani?: string;
  PlatnostOd?: string;
  CenaSDPH?: boolean | string;
  [k: string]: unknown;
}
export interface LmsEntity {
  ID: number;
  StupenVybavy?: string;
  Karoserie?: string;
  Rozvor?: string | null;
  Pohon?: string | null;
  Motor?: string;
  Prevodovka?: string;
  Oznaceni: string;
  Cena: number | string;
  [k: string]: unknown;
}
export interface LmsEquipmentColor {
  Kod?: string;
  WERS?: string;
  Nazev?: string;
  [k: string]: unknown;
}
export interface LmsEquipmentUpholstery {
  Kod?: string;
  WERS: string;
  Oznaceni: string;
  [k: string]: unknown;
}
// Covers both StandardniVybavaResponse and PriplatkovaVybavaResponse from the
// DMSAPI Ceníky/Vybava method. Per the spec only příplatková výbava carries
// `WERS` + `Cena`; standardní výbava has just Nazev/Poznamka/Skupina/Rank — so
// those are optional here. `intranet_ID` is NOT from the upstream API: our LMS
// proxy injects an auto-incrementing id onto each paid item so the UI has a
// stable checkbox key.
export interface LmsEquipmentFeature {
  Nazev: string;
  Skupina: string;
  Poznamka?: string;
  Rank?: number;
  WERS?: string;
  Cena?: number | string;
  intranet_ID?: number;
  [k: string]: unknown;
}
export interface LmsEquipment {
  barvy?: LmsEquipmentColor[];
  calouneni?: LmsEquipmentUpholstery[];
  standardniVybava?: LmsEquipmentFeature[];
  priplatkovaVybava?: LmsEquipmentFeature[];
  [k: string]: unknown;
}

/**
 * Body shared by both `save-lms-equipment` endpoints (vehicle + nabidka). The
 * server expects the full equipment payload + chosen colour/upholstery as JSON
 * strings plus the paid-extra ids.
 */
export interface LmsEquipmentSaveBody {
  vybavy?: string;
  barva?: string;
  calouneni?: string;
  priplatkove_vybavy?: number[];
  cenik_s_dph?: boolean;
}

export function useLmsModelsQuery(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['vehicles', 'lms', 'models'],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ models: LmsModel[] }>('/v1/vehicles/lms/models', {}, { signal }),
  });
}

export function useLmsModelYearsQuery(modelId: number | string | null) {
  return useQuery({
    enabled: modelId != null && modelId !== '',
    queryKey: ['vehicles', 'lms', 'model-years', modelId],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ modelYears: LmsModelYear[] }>(
        '/v1/vehicles/lms/model-years',
        { id_modelu: modelId },
        { signal },
      ),
  });
}

export function useLmsPricelistsQuery(
  modelId: number | string | null,
  modelCode: string | null,
) {
  return useQuery({
    enabled: modelId != null && modelId !== '' && !!modelCode,
    queryKey: ['vehicles', 'lms', 'pricelists', modelId, modelCode],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ pricelists: LmsPricelist[] }>(
        '/v1/vehicles/lms/pricelists',
        { id_modelu: modelId, modelID: modelCode },
        { signal },
      ),
  });
}

export function useLmsEntitiesQuery(pricelistId: number | string | null) {
  return useQuery({
    enabled: pricelistId != null && pricelistId !== '',
    queryKey: ['vehicles', 'lms', 'entities', pricelistId],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ entities: LmsEntity[]; message?: string }>(
        '/v1/vehicles/lms/entities',
        { id_ceniku: pricelistId },
        { signal },
      ),
  });
}

export function useLmsEquipmentQuery(entityId: number | string | null) {
  return useQuery({
    enabled: entityId != null && entityId !== '' && entityId !== 0,
    queryKey: ['vehicles', 'lms', 'equipment', entityId],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ equipment: LmsEquipment }>(
        '/v1/vehicles/lms/equipment',
        { id_entity: entityId },
        { signal },
      ),
  });
}

// ─── Vyhodnocení (Phase 2.8) ────────────────────────────────────────────────

export interface VyhodnoceniListFilters {
  hledanejmeno?: string;
  vin?: string;
  limit?: string;
  offset?: string;
  /** Scopes the stats charts to a single calendar year (by datum_uzavreni). */
  year?: string;
}

interface VyhodnoceniListResponse {
  smlouvy: SmlouvaListItem[];
  total: number;
}

export function useVyhodnoceniInfiniteQuery(
  filters: Omit<VyhodnoceniListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['vehicles', 'smlouvy', 'vyhodnoceni', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<VyhodnoceniListResponse>(
        `/v1/vehicles/smlouvy/vyhodnoceni${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.smlouvy.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

/**
 * Aggregate stats over the whole filtered cohort — drives the report KPIs +
 * charts so they stay correct regardless of how many table pages are loaded.
 * Time series buckets by `datum_uzavreni` per month, server-side.
 */
export interface VyhodnoceniStats {
  total: number;
  brands: number;
  latest: string | null;
  /** Distinct years present in the cohort (desc), for the chart year picker. */
  years: number[];
  byBrand: { brand: string; count: number }[];
  byMonth: { key: string; count: number }[];
}

export function useVyhodnoceniStatsQuery(
  filters: Omit<VyhodnoceniListFilters, 'limit' | 'offset'>,
) {
  return useQuery({
    queryKey: ['vehicles', 'smlouvy', 'vyhodnoceni', 'stats', filters],
    queryFn: ({ signal }) =>
      api.get<VyhodnoceniStats>(
        `/v1/vehicles/smlouvy/vyhodnoceni/stats${buildQuery(filters)}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useUpdateFleetMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id_vozidla: number; fleet_skutecnost: number }) =>
      api.post<{ status: 'ok' }>(`/v1/vehicles/smlouvy/vyhodnoceni/fleet`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', 'smlouvy', 'vyhodnoceni'] });
    },
  });
}

// ==================== FLEET POLICY (importer discount programs) ====================

export type FleetPolicyProgramTyp = 'fleet' | 'retail_ov' | 'retail_uv';

export interface FleetPolicyRadek {
  id: number;
  poradi: number;
  skupina: string | null;
  model_nazev: string;
  model_kod: string | null;
  vozidlo_model: number | null;
  podminka: string | null;
  mr_od: number | null;
  mr_do: number | null;
  pohon: string | null;
  varianta: string | null;
  vyloucene: string | null;
  pasmo_a: number | null;
  pasmo_b: number | null;
  pasmo_c: number | null;
  procento: number | null;
  kc_zvyhodneni: number | null;
  kc_fmc: number | null;
  kc_ap: number | null;
  poznamka: string | null;
}

/** Row payload for creating a version — no id/poradi (order = array order). */
export type FleetPolicyRadekInput = Omit<FleetPolicyRadek, 'id' | 'poradi'>;

export interface FleetPolicyVerze {
  id: number;
  program_typ: FleetPolicyProgramTyp;
  verze: number;
  nazev: string;
  platnost_od: string | null;
  platnost_do: string | null;
  poznamka: string | null;
  zdroj_klic: string | null;
  zdroj_nazev: string | null;
  vytvoreno: string;
  pocet: number;
}

export interface FleetPolicyResponse {
  version: FleetPolicyVerze | null;
  rows: FleetPolicyRadek[];
  versions: FleetPolicyVerze[];
  aktivniId: number | null;
}

export interface FleetPolicyDraft {
  program_typ: FleetPolicyProgramTyp;
  nazev: string;
  platnost_od: string | null;
  platnost_do: string | null;
  poznamka: string | null;
  radky: FleetPolicyRadekInput[];
}

export interface FleetPolicyExtractResponse {
  zdrojKlic: string;
  zdrojNazev: string;
  aiAvailable: boolean;
  draft: FleetPolicyDraft | null;
  chyba: string | null;
}

export type FleetPolicyVerdict = 'ok' | 'fail' | 'unknown';

export interface FleetPolicyMatchRow {
  radek: FleetPolicyRadek;
  model: FleetPolicyVerdict;
  mr: FleetPolicyVerdict;
  pohon: FleetPolicyVerdict;
}

export interface FleetPolicyMatchResponse {
  vozidlo: { modelNazev: string; modelovyRok: number | null; pohon: string | null } | null;
  programy: { version: FleetPolicyVerze; rows: FleetPolicyMatchRow[] }[];
}

export function useFleetPolicyQuery(typ: FleetPolicyProgramTyp, verze?: number, enabled = true) {
  return useQuery({
    queryKey: ['fleet-policy', typ, verze ?? 'active'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<FleetPolicyResponse>(
        `/v1/fleet-policy?typ=${typ}${verze != null ? `&verze=${verze}` : ''}`,
        { signal },
      ),
  });
}

export function useFleetPolicyCreateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      program_typ: FleetPolicyProgramTyp;
      nazev: string;
      platnost_od: string | null;
      platnost_do: string | null;
      poznamka: string | null;
      zdroj_klic: string | null;
      zdroj_nazev: string | null;
      rows: FleetPolicyRadekInput[];
    }) => api.post<{ verzeId: number; verze: number; pocet: number }>('/v1/fleet-policy', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fleet-policy'] }),
  });
}

export interface FleetPolicyExtractProgress {
  /** ulozeno = PDF stored, AI starting; cteni = model reading/thinking; radky = rows streaming in. */
  faze: 'ulozeno' | 'cteni' | 'radky';
  pocetRadku: number;
}

/**
 * The extract endpoint streams NDJSON progress events (extraction takes
 * minutes): ulozeno → prubeh/tep* → hotovo|chyba. This reads the stream,
 * relays progress via the callback, and resolves with the same final shape
 * the old JSON endpoint returned.
 */
export function useFleetPolicyExtractMutation() {
  return useMutation({
    mutationFn: async (input: {
      file: File;
      programTyp: FleetPolicyProgramTyp;
      onProgress?: (p: FleetPolicyExtractProgress) => void;
    }): Promise<FleetPolicyExtractResponse> => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('program_typ', input.programTyp);
      const res = await fetch('/api/v1/fleet-policy/extract', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers: { Accept: 'application/x-ndjson', ...getAuthHeaders() },
      });
      if (!res.ok || !res.body) {
        const text = await res.text();
        let msg = text || `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(text) as { error?: string };
          if (parsed.error) msg = parsed.error;
        } catch {
          /* keep raw text */
        }
        throw new Error(msg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let saved: { zdrojKlic: string; zdrojNazev: string; aiAvailable: boolean } | null = null;
      let final: FleetPolicyExtractResponse | null = null;

      const handleLine = (line: string) => {
        if (!line.trim()) return;
        const ev = JSON.parse(line) as {
          typ: 'ulozeno' | 'prubeh' | 'tep' | 'hotovo' | 'chyba';
          zdrojKlic?: string;
          zdrojNazev?: string;
          aiAvailable?: boolean;
          faze?: 'cteni' | 'radky';
          pocetRadku?: number;
          draft?: FleetPolicyDraft;
          chyba?: string;
        };
        switch (ev.typ) {
          case 'ulozeno':
            saved = { zdrojKlic: ev.zdrojKlic!, zdrojNazev: ev.zdrojNazev!, aiAvailable: ev.aiAvailable! };
            input.onProgress?.({ faze: 'ulozeno', pocetRadku: 0 });
            break;
          case 'prubeh':
            input.onProgress?.({ faze: ev.faze ?? 'cteni', pocetRadku: ev.pocetRadku ?? 0 });
            break;
          case 'hotovo':
            final = { ...saved!, draft: ev.draft ?? null, chyba: null };
            break;
          case 'chyba':
            final = { ...saved!, draft: null, chyba: ev.chyba ?? 'AI extrakce selhala' };
            break;
          case 'tep':
            break;
        }
      };

      // A mid-stream network failure (proxy cut, deploy cutover, flaky wifi)
      // surfaces as a read error ("Load failed"/"Failed to fetch") — swallow
      // it and fall through: the PDF is already stored, so the user gets a
      // retry-friendly message instead of a raw fetch error.
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buf.indexOf('\n')) >= 0) {
            handleLine(buf.slice(0, nl));
            buf = buf.slice(nl + 1);
          }
        }
        handleLine(buf);
      } catch {
        /* fall through to the saved/interrupted handling below */
      }

      if (final) return final;
      // Stream ended without hotovo/chyba: either AI is disabled (legitimate
      // end right after 'ulozeno') or the connection was severed mid-run.
      if (saved) {
        const s = saved as { zdrojKlic: string; zdrojNazev: string; aiAvailable: boolean };
        return {
          ...s,
          draft: null,
          chyba: s.aiAvailable
            ? 'Spojení bylo přerušeno během vytěžování — PDF je uloženo, spusťte vytěžení znovu'
            : null,
        };
      }
      throw new Error('Spojení se serverem bylo přerušeno — zkuste to znovu');
    },
  });
}

/** Active policy rows applicable to a vehicle or offer, ranked best-first. */
export function useFleetPolicyMatchQuery(target: { vozidlo?: number; nabidka?: number }, enabled = true) {
  const param = target.vozidlo != null ? `vozidlo=${target.vozidlo}` : `nabidka=${target.nabidka}`;
  return useQuery({
    queryKey: ['fleet-policy', 'match', target.vozidlo ?? null, target.nabidka ?? null],
    enabled: enabled && (target.vozidlo != null || target.nabidka != null),
    queryFn: ({ signal }) =>
      api.get<FleetPolicyMatchResponse>(`/v1/fleet-policy/match?${param}`, { signal }),
  });
}

export const FLEET_POLICY_TYP_LABEL: Record<FleetPolicyProgramTyp, string> = {
  fleet: 'Fleet policy',
  retail_ov: 'Retailový program OV',
  retail_uv: 'Retailový program UV',
};

/**
 * Open a fleet-policy version's source PDF in a new tab. The endpoint needs
 * the bearer, so a plain <a href> can't be used — fetch to a blob URL; the
 * browser previews it inline (Content-Disposition: inline). Falls back to a
 * download when the popup is blocked. Throws on failure — callers toast.
 */
export async function openFleetPolicyZdroj(verzeId: number, nazev: string | null): Promise<void> {
  const res = await fetch(`/api/v1/fleet-policy/${verzeId}/zdroj`, {
    credentials: 'same-origin',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Zdrojové PDF se nepodařilo stáhnout');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) {
    const a = document.createElement('a');
    a.href = url;
    a.download = nazev ?? 'fleet-policy.pdf';
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ────────────────────────────────────────────────────────────────────────
// Premier (ApiComPrem) — zakázky by VIN (vehicle detail integration)
// ────────────────────────────────────────────────────────────────────────

export interface PremierZakazka {
  cislo: string;
  nazev: string;
  odberatel: string;
  icoOdberatel: string;
  dicOdberatel: string;
  uliceOdberatele: string;
  pscOdberatele: string;
  mestoOdberatele: string;
  statOdberatele: string;
  konPrijem: string;
  objednano: string | null;
  planZahajeni: string | null;
  termin: string | null;
  vyrizeno: string | null;
  ukonceno: boolean;
  prubeh: string;
  poznamka: string;
  cena: number;
  stredisko: number;
  vystavil: string;
  doklad: string;
  premierId: string;
}

export interface PremierZakazkaNalezena extends PremierZakazka {
  /** 'vin' = matched the vehicle VIN in Premier; 'cislo' = fetched by a stored číslo. */
  zdrojNalezeni: 'vin' | 'cislo';
}

export interface PremierVozidloZakazkyResponse {
  zakazky: PremierZakazkaNalezena[];
  chyby: string[];
}

/**
 * All Premier zakázky of a vehicle — VIN lookup merged with stored čísla
 * (works for VIN-less cars too); the server auto-links VIN discoveries (a write
 * on a GET, and a slow on-prem round trip) — so this only runs once `enabled`:
 * the vehicle detail asks the user first. A 400 means "Premier not configured
 * for this company"; the shared QueryClient never retries 4xx.
 */
export function usePremierVozidloZakazkyQuery(idVozidla: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['premier', 'vozidlo-zakazky', idVozidla],
    enabled: enabled && idVozidla !== undefined && idVozidla > 0,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) =>
      api.get<PremierVozidloZakazkyResponse>(`/v1/premier/vozidla/${idVozidla}/zakazky`, {
        signal,
      }),
  });
}

export interface PremierUhrada {
  datum: string | null;
  castka: number;
  doklad: string;
  cislo: string;
  popis: string;
}

export interface PremierFaktura {
  doklad: string;
  cislo: string;
  partner: string;
  icoPartner: string;
  forma: string;
  datumVystaveni: string | null;
  datumSplatnosti: string | null;
  celkem: number;
  premierId: string;
  /** Payments (VAZBY → účetní deník); null = unknown (lookup failed). */
  uhrady: PremierUhrada[] | null;
}

export interface PremierSkladovyDoklad {
  doklad: string;
  cislo: string;
  popis: string;
  partner: string;
  datumVystaveni: string | null;
  celkem: number;
  brutto: number;
  sklad: number;
  skladNazev: string;
  premierId: string;
}

export interface PremierZakazkaDoklady {
  vydaneFaktury: PremierFaktura[];
  prijateFaktury: PremierFaktura[];
  vydejky: PremierSkladovyDoklad[];
  prijemky: PremierSkladovyDoklad[];
  /** "Ostatní deníky" — internal docs from PUB_UCTO; null = lookup failed. */
  ostatniDeniky: PremierDenikRadek[] | null;
  /** Vydané objednávky booked on the zakázka; null = lookup failed (unknown). */
  objednavky: PremierObjednavka[] | null;
  /** Přijaté objednávky booked on the zakázka; null = lookup failed (unknown). */
  prijate: PremierPrijataObjednavka[] | null;
  chyby: string[];
}

export interface PremierDenikRadek {
  doklad: string;
  cislo: string;
  datum: string | null;
  popis: string;
  castka: number;
  predpis: string;
  vystavil: string;
}

/**
 * Documents linked to a Premier zakázka (via ZKKOD). The sweep hits every
 * sklad on the Premier side (~40 calls server-side), so it loads lazily —
 * only when the zakázka detail modal opens.
 */
export function usePremierZakazkaDokladyQuery(cislo: string | null) {
  return useQuery({
    queryKey: ['premier', 'zakazka-doklady', cislo],
    enabled: cislo !== null && /^\d{1,10}$/.test(cislo),
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) =>
      api.get<PremierZakazkaDoklady>(`/v1/premier/zakazky/${cislo}/doklady`, { signal }),
  });
}

export interface PremierObjednavkaPolozka {
  text: string;
  mnozstvi: number;
  mj: string;
  katalog: string;
}

export interface PremierNavazanyDoklad {
  doklad: string;
  cislo: string;
}

interface PremierObjednavkaZaklad {
  /** Číslo of the zakázka it belongs to. */
  zkkod: number;
  doklad: string;
  cislo: string;
  /** Vydaná: the supplier. Přijatá: the customer. */
  partner: string;
  datumVystaveni: string | null;
  celkem: number;
  premierId: string;
  polozky: PremierObjednavkaPolozka[];
}

/** Vydaná objednávka — what we ordered from a supplier. Edge lists: null = unknown, [] = none. */
export interface PremierObjednavka extends PremierObjednavkaZaklad {
  /** Goods receipts. */
  prijato: PremierNavazanyDoklad[] | null;
  /** Supplier invoices. */
  fakturovano: PremierNavazanyDoklad[] | null;
}

/** Přijatá objednávka — what a customer ordered from us. Edge lists: null = unknown, [] = none. */
export interface PremierPrijataObjednavka extends PremierObjednavkaZaklad {
  /** Stock issues for the order. */
  vydano: PremierNavazanyDoklad[] | null;
  /** Our invoices to the customer. */
  fakturovano: PremierNavazanyDoklad[] | null;
  /** Supplier orders linked to it in Premier. */
  objednanoUDodavatele: PremierNavazanyDoklad[] | null;
}

export interface PremierVozidloObjednavkyResponse {
  /** The zakázka čísla actually searched (the server caps the list) — others are unknown. */
  zakazky: string[];
  /** null = the whole vydané lookup failed (unknown, not "none"). */
  objednavky: PremierObjednavka[] | null;
  /** null = the whole přijaté lookup failed (unknown, not "none"). */
  prijate: PremierPrijataObjednavka[] | null;
  chyby: string[];
}

/**
 * Supplier orders (vydané) and customer orders (přijaté) of the vehicle's linked
 * zakázky — "did we order it?". Hits every sklad server-side, so callers enable it only after the
 * zakázky query settled (that call also syncs VIN-discovered čísla).
 */
export function usePremierVozidloObjednavkyQuery(
  idVozidla: number | undefined,
  enabled: boolean,
  /** The zakázky čísla the page lists — part of the key so a newly linked one re-queries. */
  zakazky: string[] = [],
) {
  return useQuery({
    queryKey: ['premier', 'vozidlo-objednavky', idVozidla, [...zakazky].sort().join(',')],
    enabled: enabled && idVozidla !== undefined && idVozidla > 0,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) =>
      api.get<PremierVozidloObjednavkyResponse>(`/v1/premier/vozidla/${idVozidla}/objednavky`, {
        signal,
      }),
  });
}

export interface PremierRada {
  doklad: string;
  text: string;
}

/** Zakázka-type dokladové řady for the creation dropdown (DOKL_PU, TOK 12). */
export function usePremierRadyQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['premier', 'dokladove-rady'],
    enabled,
    staleTime: 10 * 60_000,
    queryFn: ({ signal }) =>
      api.get<{ rady: PremierRada[] }>('/v1/premier/dokladove-rady', { signal }),
  });
}

export interface PridatZakazkuBody {
  cislo: string;
  potvrdit?: boolean;
  rada?: string;
  nazev?: string;
  doplnitVin?: boolean;
}

export interface PridatZakazkuResponse {
  stav:
    | 'obsazena'
    | 'existuje-shoda'
    | 'existuje-bez-vin'
    | 'existuje-jiny-vin'
    | 'neexistuje'
    | 'propojena'
    | 'vytvorena';
  zakazka?: PremierZakazka;
  navrh?: { nazev: string; znacka: string; typ: string; provedeni: string; vin: string | null };
  obsazenoVozidlem?: string;
  cislo?: string;
  varovani: string | null;
}

/** Verify/confirm link-or-create of a Premier zakázka (see route docs). */
export function usePridatZakazkuMutation(idVozidla: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PridatZakazkuBody) =>
      api.post<PridatZakazkuResponse>(`/v1/premier/vozidla/${idVozidla}/zakazky`, body),
    onSuccess: (r) => {
      if (r.stav === 'propojena' || r.stav === 'vytvorena') {
        queryClient.invalidateQueries({ queryKey: ['premier', 'vozidlo-zakazky', idVozidla] });
        queryClient.invalidateQueries({ queryKey: ['premier', 'vozidlo-objednavky', idVozidla] });
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      }
    },
  });
}
